import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Checkbox, CheckPicker, ControlLabel, FormGroup } from 'rsuite';
import { getMonitoramentosConsumoContrato } from '../../services';
import styles from './CompartilhamentoForm.module.scss';

const MAX_MEMBROS_COMPARTILHADOS = 5;

const MENU_MAX_HEIGHT = 5 * 36;

const CONSUMO_CONTRATO_PAGE_SIZE = 200;
const getUsuarioLogadoEmail = (): string | null => {
  try {
    const email = JSON.parse(localStorage.getItem('usuario') ?? 'null')?.email;
    return typeof email === 'string' ? email.toLocaleLowerCase() : null;
  } catch {
    return null;
  }
};

type CompartilhamentoFormProps = {
  value: string[];
  onChange: (value: string[]) => void;
  hideHeader?: boolean;
};

export const CompartilhamentoForm = ({ value, onChange, hideHeader = false }: CompartilhamentoFormProps) => {
  const usuarioLogadoEmail = useMemo(getUsuarioLogadoEmail, []);
  const limiteAtingido = value.length >= MAX_MEMBROS_COMPARTILHADOS;

  const {
    data: consumoContrato,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['monitoramentos-consumo-contrato'],
    queryFn: () => getMonitoramentosConsumoContrato(0, CONSUMO_CONTRATO_PAGE_SIZE),
  });

  const membrosEquipe = useMemo(() => {
    const usuarios = consumoContrato?.data.content ?? [];
    const selecionadosPorEmail = new Map(value.map(email => [email.toLocaleLowerCase(), email]));
    const opcoesContrato = usuarios
      .filter(usuario => usuario.email.toLocaleLowerCase() !== usuarioLogadoEmail)
      .map(usuario => {
        const email = selecionadosPorEmail.get(usuario.email.toLocaleLowerCase()) ?? usuario.email;
        return { label: email, value: email };
      });
    const emailsContrato = new Set(opcoesContrato.map(opcao => opcao.value));
    const selecionadosForaDoContrato = value.filter(email => !emailsContrato.has(email)).map(email => ({ label: email, value: email }));
    return [...selecionadosForaDoContrato, ...opcoesContrato];
  }, [consumoContrato, usuarioLogadoEmail, value]);

  const membrosBloqueados = useMemo(
    () => (limiteAtingido ? membrosEquipe.filter(membro => !value.includes(membro.value)).map(membro => membro.value) : []),
    [limiteAtingido, membrosEquipe, value],
  );

  const [searchKeyword, setSearchKeyword] = useState('');
  const membrosVisiveis = useMemo(() => {
    const keyword = searchKeyword.trim().toLocaleLowerCase();
    if (!keyword) return membrosEquipe;
    return membrosEquipe.filter(membro => membro.label.toLocaleLowerCase().includes(keyword));
  }, [membrosEquipe, searchKeyword]);

  const maxSelecionavel = Math.min(membrosVisiveis.length, MAX_MEMBROS_COMPARTILHADOS);
  const allSelected = membrosVisiveis.length > 0 && membrosVisiveis.every(membro => value.includes(membro.value));

  const handleSelectAll = (_data: unknown, checked: boolean) => {
    const emailsVisiveis = membrosVisiveis.map(membro => membro.value);

    if (!checked) {
      onChange(value.filter(email => !emailsVisiveis.includes(email)));
      return;
    }

    const naoSelecionados = emailsVisiveis.filter(email => !value.includes(email));
    const vagasRestantes = Math.max(MAX_MEMBROS_COMPARTILHADOS - value.length, 0);
    onChange([...value, ...naoSelecionados.slice(0, vagasRestantes)]);
  };

  const noResultsText = isLoading
    ? 'Carregando membros...'
    : membrosEquipe.length === 0
      ? 'Você é o único usuário ativo do contrato — não há outros membros para compartilhar.'
      : 'Nenhum membro encontrado';

  return (
    <div className={hideHeader ? styles.containerFlat : styles.container}>
      {!hideHeader && (
        <div className={styles.header}>
          <h2 className={styles.title}>Compartilhamento (opcional)</h2>
          <p className={styles.subtitle}>Selecione membros para visualizar o monitoramento. Edição e exclusão não são permitidas.</p>
        </div>
      )}

      <FormGroup className={styles.formGroup}>
        <ControlLabel className={styles.label}>Compartilhar com</ControlLabel>
        <CheckPicker
          className={styles.picker}
          block
          filter
          placement="topStart"
          maxHeight={MENU_MAX_HEIGHT}
          data={membrosEquipe}
          disabledItemValues={membrosBloqueados as unknown as string[][]}
          value={value}
          onChange={onChange}
          onSearch={setSearchKeyword}
          onOpen={() => setSearchKeyword('')}
          placeholder="Selecione um ou mais membros da equipe"
          locale={{ searchPlaceholder: 'Insira nome ou e-mail', noResultsText }}
          renderMenu={menu =>
            isError ? (
              <div className={styles.errorState}>
                <p className={styles.errorStateMessage}>Não foi possível carregar a lista de membros.</p>
                <button type="button" className={styles.errorStateRetryButton} onClick={() => refetch()}>
                  Tentar novamente
                </button>
              </div>
            ) : (
              menu
            )
          }
          renderExtraFooter={() =>
            isError ? null : (
              <div className={styles.selectAllFooter}>
                <Checkbox checked={allSelected} onChange={handleSelectAll}>
                  Selecionar todos{maxSelecionavel > 0 ? ` (${maxSelecionavel})` : ''}
                </Checkbox>
                {limiteAtingido && <span className={styles.limiteAtingidoMessage}>Limite de 5 membros atingido</span>}
              </div>
            )
          }
        />
      </FormGroup>
    </div>
  );
};
